/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ListFoundryQueryRunHistoryParams,
  TLHTTPValidationError,
  TLPaginatedResponseFoundryQueryRunHistoryItem,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Foundry Query Run History
 */
export const listFoundryQueryRunHistory = (
  params?: ListFoundryQueryRunHistoryParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseFoundryQueryRunHistoryItem>(
    { url: `/foundry/query-history`, method: 'GET', params, signal },
    options,
  );
};

export const getListFoundryQueryRunHistoryQueryKey = (params?: ListFoundryQueryRunHistoryParams) => {
  return ['foundry', 'query-history', ...(params ? [params] : [])] as const;
};

export const getListFoundryQueryRunHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListFoundryQueryRunHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListFoundryQueryRunHistoryQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>> = ({ signal }) =>
    listFoundryQueryRunHistory(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListFoundryQueryRunHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>>;
export type ListFoundryQueryRunHistoryQueryError = ErrorType<TLHTTPValidationError>;

export function useListFoundryQueryRunHistory<
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListFoundryQueryRunHistoryParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
          TError,
          Awaited<ReturnType<typeof listFoundryQueryRunHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFoundryQueryRunHistory<
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListFoundryQueryRunHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
          TError,
          Awaited<ReturnType<typeof listFoundryQueryRunHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFoundryQueryRunHistory<
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListFoundryQueryRunHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Foundry Query Run History
 */

export function useListFoundryQueryRunHistory<
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListFoundryQueryRunHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFoundryQueryRunHistoryQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Foundry Query Run History
 */
export const prefetchListFoundryQueryRunHistoryQuery = async <
  TData = Awaited<ReturnType<typeof listFoundryQueryRunHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListFoundryQueryRunHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFoundryQueryRunHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListFoundryQueryRunHistoryQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Foundry Query Run History
 */
export const invalidateListFoundryQueryRunHistory = async (
  queryClient: QueryClient,
  params?: ListFoundryQueryRunHistoryParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListFoundryQueryRunHistoryQueryKey(params) }, options);

  return queryClient;
};
